import React, { useState } from 'react';
import { Upload, Check, AlertCircle, Loader } from 'lucide-react';
import '../App.css';
import { uploadImage, formatDetectionError } from '../utils/api';
import { getSelectedObjects } from '../utils/detectionSettings';

export default function ImageDetection() {
  const [selectedImage, setSelectedImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detections, setDetections] = useState(null);
  const [error, setError] = useState(null);
  const [confidence, setConfidence] = useState(0.5);

  const classCounts = detections?.detections?.reduce((counts, detection) => {
    counts[detection.class] = (counts[detection.class] || 0) + 1;
    return counts;
  }, {}) || {};

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const handleDetect = async () => {
    if (!selectedImage) {
      setError('Please select an image first');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setDetections(null);

      const response = await uploadImage(selectedImage, confidence, getSelectedObjects());

      if (response.success) {
        setDetections(response);
      }
    } catch (err) {
      setError(formatDetectionError(err));
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!preview || !detections || detections.detections.length === 0) {
      return;
    }

    const img = new Image();
    img.onload = () => {
      const canvas = document.getElementById('detection-canvas');
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      canvas.width = img.width;
      canvas.height = img.height;

      ctx.drawImage(img, 0, 0);

      detections.detections.forEach((det) => {
        const [x1, y1, x2, y2] = det.bbox;
        ctx.strokeStyle = '#22d3ee';
        ctx.lineWidth = 2;
        ctx.shadowColor = 'rgba(8, 145, 178, 0.35)';
        ctx.shadowBlur = 6;
        ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
        ctx.shadowBlur = 0;
      });
    };
    img.src = preview;
  }, [detections, preview]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <h1 className="text-3xl font-bold text-white">Image Detection</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload Section */}
        <div className="space-y-6">
          <div className="glass-panel p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Upload Image</h3>

            {/* Drag and Drop */}
            <div
              className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center hover:border-cyan-400 transition-colors cursor-pointer"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleImageSelect({ target: { files: e.dataTransfer.files } });
              }}
            >
              <input
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
                id="image-input"
              />
              <label htmlFor="image-input" className="cursor-pointer block">
                <Upload className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                <p className="text-white font-medium">Drag and drop or click to select</p>
                <p className="text-gray-400 text-sm mt-1">Supported: JPG, PNG, GIF, BMP</p>
              </label>
            </div>

            {/* Confidence Slider */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Confidence Threshold: {(confidence * 100).toFixed(0)}%
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={confidence}
                onChange={(e) => setConfidence(parseFloat(e.target.value))}
                disabled={loading}
                className="w-full"
              />
            </div>

            <div className="mt-4 rounded-lg border border-slate-700 bg-slate-900/70 px-4 py-3 text-sm text-gray-300">
              Detecting classes: <span className="text-cyan-400">{getSelectedObjects().join(', ')}</span>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 space-y-3">
              <button
                onClick={handleDetect}
                disabled={!selectedImage || loading}
                className="w-full btn-primary flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <><Loader className="w-4 h-4 animate-spin" /><span>Detecting... (may take ~30s on first run)</span></>
                ) : (
                  <><Check className="w-4 h-4" /><span>Detect Objects</span></>
                )}
              </button>

              {selectedImage && (
                <button
                  onClick={() => {
                    setSelectedImage(null);
                    setPreview(null);
                    setDetections(null);
                  }}
                  className="w-full btn-secondary"
                >
                  Clear
                </button>
              )}
            </div>

            {error && (
              <div className="mt-4 p-4 bg-red-500 bg-opacity-10 border border-red-500 rounded-lg flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}
          </div>

          {/* Detection Results */}
          {detections && (
            <div className="glass-panel p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Detection Results</h3>

              <div className="space-y-4">
                <div className="bg-slate-800 rounded-lg p-4">
                  <p className="text-gray-400 text-sm mb-1">Total Detections</p>
                  <p className="text-3xl font-bold text-cyan-400">{detections.count}</p>
                </div>

                <div>
                  <p className="text-gray-400 text-sm mb-3">Objects By Class:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {Object.entries(classCounts).map(([className, count]) => (
                      <div key={className} className="flex items-center justify-between rounded-lg bg-slate-800 p-3">
                        <span className="text-sm text-white capitalize">{className}</span>
                        <span className="detection-badge">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-gray-400 text-sm mb-3">Detected Objects:</p>
                  <div className="space-y-2">
                    {detections.detections.map((det, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                        <div>
                          <p className="text-sm text-white">
                            {det.class.charAt(0).toUpperCase() + det.class.slice(1)} #{idx + 1}
                          </p>
                          <p className="text-xs text-gray-400">
                            Position: ({det.bbox[0].toFixed(0)}, {det.bbox[1].toFixed(0)})
                          </p>
                        </div>
                        <span className="detection-badge">
                          {(det.confidence * 100).toFixed(1)}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="text-xs text-gray-400">
                  <p>Timestamp: {new Date(detections.timestamp).toLocaleString()}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Preview Section */}
        <div className="glass-panel p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Preview</h3>

          {preview ? (
            <div className="space-y-4">
              {detections ? (
                <canvas
                  id="detection-canvas"
                  className="w-full rounded-lg border border-slate-600"
                />
              ) : (
                <img
                  src={preview}
                  alt="Preview"
                  className="w-full rounded-lg border border-slate-600"
                />
              )}

              {selectedImage && (
                <div className="p-4 bg-slate-800 rounded-lg">
                  <p className="text-sm text-gray-400 mb-1">Filename</p>
                  <p className="text-white font-medium break-all">{selectedImage.name}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Size: {(selectedImage.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="h-96 flex items-center justify-center bg-slate-800 rounded-lg">
              <div className="text-center">
                <Upload className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No image selected</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
